<template>
    <template v-if="pendingTerm">
        <vx-alert shadow data-test="terms-pre-effective-alert" color="info" :closeable="false">
            <div class="flex flex-col gap-2">
                <div class="font-extrabold text-base">{{ pendingTerm.title }}</div>
                <div>
                    <template v-if="isOwner">
                        <template v-if="hasTakenEffect">
                            New Terms &amp; Conditions are now in effect. Review and accept now to keep using this
                            group without interruption.
                        </template>
                        <template v-else>
                            New Terms &amp; Conditions take effect on {{ formattedEffectiveAt }}. Review and accept
                            before then to keep using this group without interruption.
                        </template>
                    </template>
                    <template v-else>
                        <template v-if="hasTakenEffect">
                            New Terms &amp; Conditions are now in effect. Your group owner must accept them to avoid
                            interruption.
                        </template>
                        <template v-else>
                            New Terms &amp; Conditions take effect on {{ formattedEffectiveAt }}. Your group owner must
                            accept them before then to avoid interruption.
                        </template>
                    </template>
                </div>
            </div>
            <template v-slot:right>
                <vx-button
                    v-if="isOwner"
                    data-test="terms-open-owner-modal"
                    color="info"
                    @click="ownerModalVisible = true"
                >
                    Review &amp; Accept
                </vx-button>
                <vx-button
                    v-else-if="owner"
                    data-test="terms-contact-owner-link"
                    color="info"
                    :href="`mailto:${owner.email}`"
                >
                    Contact {{ owner.name }}
                </vx-button>
            </template>
        </vx-alert>
    </template>

    <vx-modal
        v-if="pendingTerm && isOwner"
        v-model:visible="ownerModalVisible"
        data-test="terms-owner-modal"
        padding="standard"
        size="lg"
    >
        <template v-slot:header>{{ pendingTerm.title }}</template>
        <markdown-renderer :source="pendingTerm.content" />
        <div class="mt-6 bg-white rounded p-4 sticky left-0 bottom-0 shadow text-slate-800 flex items-start gap-3">
            <vx-checkbox v-model="consentChecked" data-test="terms-consent-checkbox" />
            <label class="text-sm cursor-pointer" @click="consentChecked = !consentChecked">
                {{ pendingTerm.consent_text }}
            </label>
        </div>
        <template v-slot:footer>
            <div class="flex justify-end gap-2">
                <vx-button size="lg" color="muted" @click="ownerModalVisible = false">Cancel</vx-button>
                <vx-popover arrow placement="top" hover class="grow">
                    <vx-button
                        block
                        color="primary"
                        :disabled="!consentChecked"
                        :loading="submitting"
                        size="lg"
                        data-test="terms-submit-button"
                        @click="submit"
                    >
                        {{ pendingTerm.consent_button }}
                    </vx-button>
                    <template v-if="!consentChecked" v-slot:content
                        >You must check "{{ pendingTerm.consent_text }}" before proceeding</template
                    >
                </vx-popover>
            </div>
        </template>
    </vx-modal>
</template>

<script lang="ts" setup>
import { computed, onMounted, ref, watch } from 'vue';
import { VxAlert, VxButton, VxCheckbox, VxModal, VxPopover } from '@voxie/frontend-components';
import MarkdownRenderer from '~/components/general/MarkdownRenderer.vue';
import termsClient from '~/services/termsClient';
import { useToasted } from '~/composables/useToasted';
import type { GroupOwnerContact, Term } from '~/services/termsClient.types';
import { catchRequestError } from '~/utils/request';
import { shortDate } from '~/utils/date';
import dayjs from '~/utils/dayjs';

const toasted = useToasted();

const teamId = Spark.state.currentTeam.id;
const groupId = Spark.state.currentGroup?.group_id ?? null;
const isOwner = Spark.roleOnCurrentGroup === 'owner';

const pendingTerm = ref<Term | null>(null);
const owner = ref<GroupOwnerContact | null>(null);
const ownerModalVisible = ref(false);
const consentChecked = ref(false);
const submitting = ref(false);

const formattedEffectiveAt = computed(() => {
    if (!pendingTerm.value) {
        return '';
    }
    return shortDate(pendingTerm.value.effective_at);
});

const hasTakenEffect = computed(() =>
    pendingTerm.value ? dayjs.utc(pendingTerm.value.effective_at).isBefore(dayjs()) : false
);

const loadActive = async () => {
    if (!groupId) {
        return;
    }

    try {
        const { data } = await termsClient.getActiveTerm(teamId, groupId);
        pendingTerm.value = data.accepted ? null : data.term;
        owner.value = data.owner;
    } catch (error) {
        console.error('Failed to load active T&C', error);
        pendingTerm.value = null;
        owner.value = null;
    }
};

const submit = async () => {
    if (!pendingTerm.value || !groupId || !isOwner || !consentChecked.value) {
        return;
    }

    submitting.value = true;

    try {
        await termsClient.acceptTerm(teamId, groupId, pendingTerm.value.id);
        pendingTerm.value = null;
        ownerModalVisible.value = false;
        consentChecked.value = false;
        toasted.success('Terms & Conditions accepted successfully');
    } catch (error: unknown) {
        toasted.error(catchRequestError(error));
    } finally {
        submitting.value = false;
    }
};

watch(ownerModalVisible, (isVisible) => {
    if (!isVisible) {
        consentChecked.value = false;
    }
});

onMounted(() => {
    loadActive();
});
</script>
